/**
 * Client-side compiler for the QuickBooks "rebuild" import (issue #212).
 *
 * Takes the parsed journal (utils/journal-csv-parser) plus in-memory
 * resolution maps (GL codes / accounts / vendors / banks — small tables the
 * client already holds) and compiles the ENTIRE transactional insert graph:
 * journal entries + lines, projected domain rows (charges/payments/bills/…),
 * and FIFO payment/credit applications — every row carrying a client-generated
 * id, ready for the server's dumb bulk-loader.
 *
 * This is a PURE port of the server projection pipeline
 * (api/accounting/project-from-journal.ts + the posting funnel's column
 * shapes). The classification rules, allocation strategy (exact-amount match
 * then capped FIFO), synthetic deposits, stub bills, returned-payment
 * matching, and cleared_at stamping are kept rule-for-rule identical — the
 * difference is that every "DB lookup" resolves against the in-memory maps
 * and every "INSERT" appends to the compiled row set.
 *
 * Allowed because this path is SUPER-ADMIN-ONLY and INITIALIZATION-ONLY: the
 * server still enforces scoping, payload version, balance checks, and an
 * init-only guard, but trusts the row content (see qb-compiled loader).
 *
 * Determinism: `crypto.randomUUID` is injectable (opts.newId) and `now` is an
 * input, so a compile is reproducible in tests. Entries are processed in
 * (posted_on, Trans #) order — the same order the server pipeline drains its
 * staging table — so FIFO application matches the server behavior.
 */

import type { StandardGl } from '@/utils/gl-standard-match';
import { type ParsedEntry, type ParsedLine, parseAccountCell } from '@/utils/journal-csv-parser';
import { parseCustomerName } from '@/utils/qb-customer-match';
import { extractShortcode } from '@/utils/shortcode';

// v3: gl_codes is a global-only chart — the compiled gl_codes rows no longer
// carry association_id (the column was dropped). Bumping rejects any stale v1/v2
// bundle in R2 and forces a fresh compile against the new schema.
// v4: the normalized `checks` table — the compiler now emits one `checks` row
// per (bank_account_id, check_number) among the bundle's check-method bill
// payments and stamps `bill_payments.check_id`. Bumping rejects any stale v3
// bundle whose payments would land with a NULL check_id (unlinked from the
// checks model).
export const QB_COMPILED_PAYLOAD_VERSION = 4;

// ────────────────────────────────────────────────────────────────────────────
// Inputs
// ────────────────────────────────────────────────────────────────────────────

export interface QbCompileGlCode {
	id: string;
	accountType: string;	// gl_codes.account_type (BANK / AR / AP / INC / EXP / …)
	name: string;
}

/**
 * Index GL rows for the resolver, keyed by BOTH `code` and the original
 * QuickBooks number (`external_id`). Real codes take precedence over aliases, so
 * a renumbered standard (code 8050, external_id "8500") lets an incoming QB line
 * referencing 8500 resolve to it instead of spawning a per-association GL row —
 * the mechanism that migrates clients onto the standard chart.
 */
export const buildGlByCode = (
	rows: Array<{ id: string; code?: string | null; name?: string | null; accountType?: string | null; externalId?: string | null }>,
): Map<string, QbCompileGlCode> => {
	const map = new Map<string, QbCompileGlCode>();
	const entryOf = (r: { id: string; name?: string | null; code?: string | null; accountType?: string | null }): QbCompileGlCode =>
		({ id: r.id, accountType: (r.accountType ?? 'OTHER').toUpperCase(), name: r.name ?? r.code ?? '' });
	// First-write-wins mirrors the resolver's association-scoped preference
	// (scoped rows are ordered before template defaults by the caller).
	for (const r of rows) {
		if (r.code && !map.has(r.code)) map.set(r.code, entryOf(r));
	}
	// QB-number aliases, only where they don't shadow a real code.
	for (const r of rows) {
		if (r.externalId && !map.has(r.externalId)) map.set(r.externalId, entryOf(r));
	}
	return map;
};

/** One row in the import's GL-code mapping preview (incoming QB code → target). */
export interface GlCodeMapping {
	/** The GL code as written in the QuickBooks journal. */
	incomingCode: string;
	/** The QB account name from the journal (or the resolved account's name). */
	incomingName: string;
	/** How many journal lines reference this code. */
	refCount: number;
	/** The gl_codes.id this code currently resolves to, or null = would create new. */
	resolvedGlId: string | null;
	/** Display label for the resolved target ("8050 · POOL"), or null when unresolved. */
	resolvedLabel: string | null;
}

/** A user's override for one incoming GL code: map to `glId`, or null = create new. */
export interface GlCodeResolution {
	incomingCode: string;
	glId: string | null;
}

/**
 * How a GL-code mapping row relates to the standard chart, for the review UI's
 * highlighting:
 *   - 'new'       → will create a new code (no target selected)
 *   - 'exact'     → maps to the SAME-numbered standard code (safe; no attention)
 *   - 'suggested' → maps to a DIFFERENT-numbered code (fuzzy/rename match — review)
 */
export type GlMatchKind = 'new' | 'exact' | 'suggested';

export const glMappingMatchKind = (
	resolvedGlId: string | null,
	incomingCode: string,
	standardById: Map<string, { code: string }>,
): GlMatchKind => {
	if (!resolvedGlId) return 'new';
	const std = standardById.get(resolvedGlId);
	return std && std.code === incomingCode ? 'exact' : 'suggested';
};

/**
 * Enumerate the distinct GL codes the journal references and how each resolves
 * against the current resolution map (which already reflects the standard chart,
 * since the IIF lists are applied before this runs). Powers the import's
 * mapping-preview table; the user can then override any row before the compile.
 */
export const probeGlCodeMappings = (
	entries: ParsedEntry[],
	resolutions: QbCompileResolutions,
	standardDefaults: StandardGl[],
): GlCodeMapping[] => {
	const stdById = new Map(standardDefaults.map((d) => [d.id, d]));
	const seen = new Map<string, GlCodeMapping>();
	for (const e of entries) {
		for (const l of e.lines) {
			const code = l.accountCode;
			if (!code || code === CLEARING_TOKEN) continue;
			const hit = seen.get(code);
			if (hit) { hit.refCount++; continue; }
			const resolved = resolutions.glByCode.get(code) ?? null;
			const std = resolved ? stdById.get(resolved.id) : undefined;
			seen.set(code, {
				incomingCode: code,
				incomingName: l.accountName || resolved?.name || '',
				refCount: 1,
				resolvedGlId: resolved?.id ?? null,
				resolvedLabel: std ? `${std.code} · ${std.name}` : (resolved?.name ?? null),
			});
		}
	}
	return [...seen.values()];
};

/**
 * Fold the user's GL-code overrides into the resolution map before the real
 * compile. `glId` set → bind the incoming code to that standard account; null →
 * drop any resolution so the compiler's ensureGl creates a fresh code. Mutates
 * `resolutions.glByCode` (the compiler's input).
 */
export const applyGlCodeResolutions = (
	resolutions: QbCompileResolutions,
	decisions: GlCodeResolution[],
	standardDefaults: StandardGl[],
): void => {
	const stdById = new Map(standardDefaults.map((d) => [d.id, d]));
	for (const d of decisions) {
		if (d.glId) {
			const std = stdById.get(d.glId);
			if (std) resolutions.glByCode.set(d.incomingCode, { id: std.id, accountType: std.accountType, name: std.name });
		} else {
			resolutions.glByCode.delete(d.incomingCode);
		}
	}
};

export interface QbCompileResolutions {
	/** GL code string (e.g. '1010') → gl_codes row. */
	glByCode: Map<string, QbCompileGlCode>;
	/** Account-number → accounts.id. Historically called "shortcode" because
	 *  shortcode-style associations set account_number to the unit shortcode
	 *  (e.g. '2371PD'); associations that key owners by a plain number use the
	 *  bare number ('101'). Used as the fallback when `resolveAccountId` is unset
	 *  or returns null. */
	accountIdByShortcode: Map<string, string>;
	/** Preferred A/R owner resolver: maps a QB "Name" (the account display name,
	 *  e.g. '1 - ` Mittiga') → accounts.id using the shared matchSection
	 *  convention (external_name exact, account-number prefix, owner-name
	 *  tie-break). Injected by the client so the compiler stays pure. When unset
	 *  or it returns null, the compiler falls back to the display-name
	 *  account-number prefix against `accountIdByShortcode`. */
	resolveAccountId?: (qbCustomerName: string | null | undefined) => string | null;
	/** LOWER(vendor name) → vendors.id. */
	vendorIdByLowerName: Map<string, string>;
	/** gl_codes.id → existing bank_accounts.id (compiler creates missing ones). */
	bankAccountIdByGlCodeId: Map<string, string>;
	/** Association default fund (journal_lines.fund_id). */
	defaultFundId: string | null;
	/** Unresolved income/expense GL codes fall back to these (association's
	 *  unassigned buckets) — mirrors the server importer's behavior. */
	fallbackIncomeGlId?: string | null;
	fallbackExpenseGlId?: string | null;
}

export interface QbCompileOptions {
	associationId: string;
	importBatchId: string;
	markCleared: boolean;
	now: number;
	createdBy?: string | null;
	/** Injectable id factory for deterministic tests. */
	newId?: (prefix: string) => string;
	/**
	 * Probe mode: instead of erroring on a bill/bill-payment whose payee isn't a
	 * known vendor, record the payee on `unresolvedVendors` and route the entry
	 * to journal-only. The UI runs a probe compile first, prompts the user to
	 * create-or-map each unknown payee, merges the decisions into the vendor
	 * resolution map, then re-compiles for real (every vendor now resolves).
	 */
	collectUnresolvedVendors?: boolean;
	/**
	 * Probe mode for OWNERS: instead of silently auditing an A/R entry whose QB
	 * "Name" doesn't resolve to an owner account, record the name on
	 * `unresolvedOwners`. The UI runs a probe compile and BLOCKS on the result so
	 * unmatched customer transactions can never be dropped under a "success"
	 * banner (the West Harbor incident). The entry is still thrown/audited — the
	 * probe only needs the names.
	 */
	collectUnresolvedOwners?: boolean;
}

/** A payee referenced by a bill/bill-payment that isn't a known vendor yet. */
export interface UnresolvedVendor {
	/** Payee name verbatim from the QB Name column (trimmed). */
	name: string;
	/** QB transaction type that referenced it (e.g. "Check", "Bill"). */
	qbType: string;
	/** One example dollar amount (cents) to give the prompt context. */
	sampleCents: number;
	/** How many entries reference this payee. */
	count: number;
}

/** An A/R (owner) QB "Name" that didn't resolve to any account. Same shape as
 *  UnresolvedVendor — the modal renders both with the same review list. */
export interface UnresolvedOwner {
	/** Owner display name verbatim from the QB Name column (trimmed). */
	name: string;
	/** QB transaction type that referenced it (e.g. "Invoice", "Payment"). */
	qbType: string;
	/** One example dollar amount (cents) to give the prompt context. */
	sampleCents: number;
	/** How many A/R entries reference this unmatched owner. */
	count: number;
}

/** One detail-report row, as fed to enrichment / auto-correction. */
export interface QbDetailRow {
	/** Customer (AR) or vendor (AP) section name. */
	partyName: string;
	/** 'Invoice' | 'Payment' | 'Credit Memo' | 'Bill' | 'Bill Pmt -Check' */
	type: string;
	/** QB Trans # — the exact join key. Rows without one can't match. */
	transId?: string;
	/** UTC-midnight-ish ms (parser-local); used only for synthesized entries. */
	dateMs: number | null;
	num?: string;
	memo?: string;
	/** Humanized charge category (Split-derived; absent in the full layout). */
	category?: string;
	/** SIGNED dollars from the report (Invoice +, Payment −, Bill +, Pmt −). */
	amount: number;
	/** AR/AP control account cell, e.g. "1200 · ACCOUNTS RECEIVABLE". */
	account?: string;
}

export interface QbCompileDetails {
	ar?: QbDetailRow[];
	ap?: QbDetailRow[];
	/**
	 * Phase 6 — OPT-IN journal auto-correction: detail rows whose Trans # is
	 * MISSING from the journal export (truncated year files, backdated history
	 * outside the export range) are synthesized as correcting journal entries
	 * so AR/AP balances tie out to QuickBooks even with an imperfect export.
	 * The unknown cash leg posts to `clearingGl` (a balance-sheet
	 * clearing/suspense GL — NEVER income/expense, so the P&L stays honest).
	 * Every synthesized entry is visibly flagged with an "[auto-correction]"
	 * memo prefix and counted in stats.
	 */
	autoCorrect?: { clearingGl: QbCompileGlCode } | null;
}

export interface QbEnrichmentStats {
	arMatched: number;
	arUnmatched: number;
	arCorrected: number;
	apMatched: number;
	apUnmatched: number;
	apCorrected: number;
}

/** Reserved account-code token resolveLines maps to the correction clearing GL. */
const CLEARING_TOKEN = '__qb_correction_clearing__';

// ────────────────────────────────────────────────────────────────────────────
// Output row shapes — column objects keyed by DB column name, insertion-ready.
// ────────────────────────────────────────────────────────────────────────────

export type CompiledRow = Record<string, string | number | null>;

/** Tables in FK-safe insert order. The loader MUST insert in this order.
 *  deposits precede payments — payments.deposit_id references the synthetic
 *  deposit the compiler wraps direct-to-bank payments in; gl_codes precede
 *  everything (journal_lines/bank_accounts/etc. reference them). */
export const COMPILED_TABLE_ORDER = [
	'gl_codes',
	'bank_accounts',
	'journal_entries',
	'journal_lines',
	'charges',
	'deposits',
	'payments',
	'bills',
	'bill_line_items',
	// checks precede bill_payments — bill_payments.check_id references the one
	// physical check the compiler groups check-method payments under.
	'checks',
	'bill_payments',
	'credits',
	'refunds',
	'bank_transfers',
	'returned_payments',
	'payment_applications',
	'credit_applications',
	'projection_audit',
] as const;

export type CompiledTable = (typeof COMPILED_TABLE_ORDER)[number];

/**
 * The versioned column contract per table — the loader inserts EXACTLY these
 * columns (whitelist: row keys outside this list are ignored, missing keys
 * bind NULL). Shared between compiler and loader so the two can never drift;
 * any change here REQUIRES bumping QB_COMPILED_PAYLOAD_VERSION.
 */
export const COMPILED_COLUMNS: Record<CompiledTable, readonly string[]> = {
	gl_codes: ['id', 'code', 'name', 'full_path', 'account_type', 'active', 'sort_order', 'import_batch_id', 'created_at', 'updated_at'],
	bank_accounts: ['id', 'association_id', 'name', 'kind', 'current_balance_cents', 'is_default', 'active', 'gl_code_id', 'created_at', 'updated_at'],
	journal_entries: ['id', 'association_id', 'posted_on', 'memo', 'source', 'source_ref', 'reverses_entry_id', 'created_by', 'created_at', 'updated_at', 'import_batch_id', 'cleared_at'],
	journal_lines: ['id', 'entry_id', 'sort_order', 'gl_code_id', 'fund_id', 'debit_cents', 'credit_cents', 'memo', 'created_at', 'account_id', 'bank_account_id', 'vendor_id'],
	charges: ['id', 'account_id', 'posted_on', 'due_on', 'amount_cents', 'paid_cents', 'category', 'memo', 'source', 'source_ref', 'source_entry_id', 'created_at', 'updated_at', 'gl_code_id'],
	payments: ['id', 'account_id', 'received_on', 'amount_cents', 'method', 'reference', 'memo', 'created_at', 'updated_at', 'bank_account_id', 'cleared_at', 'source_entry_id', 'deposit_id', 'returned_at'],
	deposits: ['id', 'association_id', 'bank_account_id', 'deposited_on', 'memo', 'total_cents', 'cleared_at', 'created_at', 'updated_at', 'source_entry_id'],
	bills: ['id', 'association_id', 'vendor_id', 'bill_number', 'issued_on', 'due_on', 'total_cents', 'paid_cents', 'status', 'memo', 'source', 'source_ref', 'source_entry_id', 'is_imported_stub', 'import_batch_id', 'created_at', 'updated_at'],
	bill_line_items: ['id', 'bill_id', 'sort_order', 'description', 'gl_code_id', 'amount_cents', 'memo', 'created_at', 'updated_at'],
	checks: ['id', 'bank_account_id', 'association_id', 'check_number', 'vendor_id', 'check_date', 'memo', 'amount_cents', 'created_by', 'created_at', 'updated_at'],
	bill_payments: ['id', 'bill_id', 'bank_account_id', 'paid_on', 'amount_cents', 'method', 'check_number', 'check_id', 'reference', 'memo', 'cleared_at', 'created_at', 'updated_at', 'source_entry_id'],
	credits: ['id', 'account_id', 'issued_on', 'amount_cents', 'memo', 'created_at', 'updated_at', 'gl_code_id', 'credit_number', 'source_entry_id'],
	refunds: ['id', 'account_id', 'issued_on', 'amount_cents', 'method', 'memo', 'created_at', 'updated_at', 'gl_code_id', 'source_entry_id'],
	bank_transfers: ['id', 'association_id', 'from_bank_id', 'to_bank_id', 'transferred_on', 'amount_cents', 'memo', 'reference', 'from_cleared_at', 'to_cleared_at', 'created_at', 'updated_at', 'source_entry_id'],
	returned_payments: ['id', 'association_id', 'payment_id', 'account_id', 'bank_account_id', 'returned_on', 'amount_cents', 'reason', 'source_entry_id', 'cleared_at', 'memo', 'created_at', 'updated_at'],
	payment_applications: ['id', 'payment_id', 'charge_id', 'amount_cents_applied', 'created_at'],
	credit_applications: ['id', 'credit_id', 'charge_id', 'amount_cents_applied', 'created_at'],
	projection_audit: ['id', 'import_batch_id', 'association_id', 'entry_id', 'classification', 'reason', 'created_at'],
};

/**
 * Each compiled table's foreign-key columns → the table they reference. Used by
 * the loader to turn a bare "FOREIGN KEY constraint failed" into a message that
 * names the exact dangling column + value (which reference is missing). Only
 * columns present in COMPILED_COLUMNS are listed.
 */
export const COMPILED_FK_REFS: Partial<Record<CompiledTable, Record<string, string>>> = {
	bank_accounts: { gl_code_id: 'gl_codes' },
	journal_entries: { reverses_entry_id: 'journal_entries' },
	journal_lines: {
		entry_id: 'journal_entries', gl_code_id: 'gl_codes', fund_id: 'funds',
		account_id: 'accounts', bank_account_id: 'bank_accounts', vendor_id: 'vendors',
	},
	charges: { account_id: 'accounts', gl_code_id: 'gl_codes', source_entry_id: 'journal_entries' },
	payments: { account_id: 'accounts', bank_account_id: 'bank_accounts', deposit_id: 'deposits', source_entry_id: 'journal_entries' },
	deposits: { bank_account_id: 'bank_accounts', source_entry_id: 'journal_entries' },
	bills: { vendor_id: 'vendors', source_entry_id: 'journal_entries' },
	bill_line_items: { bill_id: 'bills', gl_code_id: 'gl_codes' },
	checks: { bank_account_id: 'bank_accounts', vendor_id: 'vendors', association_id: 'associations' },
	bill_payments: { bill_id: 'bills', bank_account_id: 'bank_accounts', check_id: 'checks', source_entry_id: 'journal_entries' },
	credits: { account_id: 'accounts', gl_code_id: 'gl_codes', source_entry_id: 'journal_entries' },
	refunds: { account_id: 'accounts', gl_code_id: 'gl_codes', source_entry_id: 'journal_entries' },
	bank_transfers: { from_bank_id: 'bank_accounts', to_bank_id: 'bank_accounts', source_entry_id: 'journal_entries' },
	returned_payments: { payment_id: 'payments', account_id: 'accounts', bank_account_id: 'bank_accounts', source_entry_id: 'journal_entries' },
	payment_applications: { payment_id: 'payments', charge_id: 'charges' },
	credit_applications: { credit_id: 'credits', charge_id: 'charges' },
	projection_audit: { entry_id: 'journal_entries' },
};

/** NDJSON line shapes for the compiled artifact parts uploaded to R2. */
export interface CompiledHeaderLine {
	t: '__header';
	v: number;
	associationId: string;
	/** Total rows across ALL parts, by table — the loader verifies counts. */
	counts: Record<string, number>;
}
export interface CompiledRowLine {
	t: CompiledTable;
	r: CompiledRow;
}

/**
 * Serialize a compiled result into ordered NDJSON part payloads (~`linesPerPart`
 * rows each). Part 0 starts with the header line; rows stream in
 * COMPILED_TABLE_ORDER so any prefix of parts is FK-consistent (parents land
 * before children).
 *
 * Part boundaries NEVER split one journal entry's lines: the loader enforces
 * per-entry debits==credits within each part, so a mid-entry split would read
 * as an unbalanced entry. A part may therefore overflow `linesPerPart`
 * slightly to finish the in-flight entry.
 */
export function serializeCompiledBundle(result: QbCompiledResult, linesPerPart = 5_000): string[] {
	const counts: Record<string, number> = {};
	for (const t of COMPILED_TABLE_ORDER) counts[t] = result.rows[t].length;
	const header: CompiledHeaderLine = {
		t: '__header',
		v: result.payloadVersion,
		associationId: result.associationId,
		counts,
	};
	const parts: string[] = [];
	let current: string[] = [JSON.stringify(header)];
	let lastEntryId: string | null = null;
	const flushIfFull = (nextEntryId: string | null) => {
		if (current.length < linesPerPart) return;
		// journal_lines continuing the same entry must stay in this part.
		if (nextEntryId != null && nextEntryId === lastEntryId) return;
		parts.push(current.join('\n'));
		current = [];
	};
	for (const t of COMPILED_TABLE_ORDER) {
		for (const r of result.rows[t]) {
			const entryId = t === 'journal_lines' ? String(r.entry_id) : null;
			flushIfFull(entryId);
			current.push(JSON.stringify({ t, r }));
			lastEntryId = entryId;
		}
	}
	if (current.length > 0) parts.push(current.join('\n'));
	return parts;
}

export interface QbCompiledAuditRow {
	transactionId: string;
	qbType: string;
	postedOn: number;
	reason: string;
}

export interface QbCompiledResult {
	payloadVersion: typeof QB_COMPILED_PAYLOAD_VERSION;
	associationId: string;
	rows: Record<CompiledTable, CompiledRow[]>;
	/** Entries that posted to the journal but produced no domain row. */
	audit: QbCompiledAuditRow[];
	/**
	 * Payees referenced by bills/bill-payments that aren't known vendors.
	 * Only populated when `options.collectUnresolvedVendors` is set (probe run);
	 * empty on a normal compile. Distinct by lower(name).
	 */
	unresolvedVendors: UnresolvedVendor[];
	/**
	 * A/R owner names that didn't resolve to an account. Only populated when
	 * `options.collectUnresolvedOwners` is set (probe run); empty on a normal
	 * compile. Distinct by lower(name).
	 */
	unresolvedOwners: UnresolvedOwner[];
	/** Detail-report join + auto-correction tallies (when details provided). */
	enrichment?: QbEnrichmentStats;
	stats: {
		entries: number;
		lines: number;
		byProjection: Record<string, number>;
		unclassified: number;
		banksCreated: number;
		/** Per bank_account_id ending balance (Σ debit−credit on its lines). */
		bankEndingCents: Record<string, number>;
	};
}

// ────────────────────────────────────────────────────────────────────────────
// Internal working state
// ────────────────────────────────────────────────────────────────────────────

const BANK_TYPES = new Set(['BANK']);
const AR_TYPES = new Set(['AR']);
const AP_TYPES = new Set(['AP']);

const QB_TYPE_TO_PROJECTION: Record<string, ProjectionType> = {
	'Invoice': 'charge',
	'Bill': 'bill',
	'Bill Pmt -Check': 'bill_payment',
	'Check': 'bill_payment',
	'Credit Memo': 'credit',
	'Payment': 'payment',
	'Deposit': 'deposit',
	'Transfer': 'bank_transfer',
};

export type ProjectionType =
	| 'charge' | 'payment' | 'bill' | 'bill_payment'
	| 'credit' | 'refund' | 'deposit' | 'bank_transfer' | 'returned_payment';

interface ResolvedLine {
	glCodeId: string | null;
	accountType: string | null;
	accountId: string | null;
	bankAccountId: string | null;
	debitCents: number;
	creditCents: number;
	memo: string | null;
	sortOrder: number;
	qbName: string | null;
}

/** In-memory open-charge state for FIFO application. */
interface CompiledCharge {
	id: string;
	accountId: string;
	postedOn: number;
	amountCents: number;
	paidCents: number;
	seq: number;	// insertion order = created_at tiebreaker
}

interface CompiledBill {
	id: string;
	vendorId: string;
	issuedOn: number;
	totalCents: number;
	paidCents: number;
	seq: number;
	row: CompiledRow;	// live reference — paid_cents/status finalized at the end
}

interface CompiledPayment {
	id: string;
	accountId: string;
	receivedOn: number;
	amountCents: number;
	appliedCents: number;
	returnedAt: number | null;
	reference: string | null;
	row: CompiledRow;
}

class Compiler {
	readonly rows: Record<CompiledTable, CompiledRow[]> = {
		gl_codes: [], bank_accounts: [], journal_entries: [], journal_lines: [],
		charges: [], payments: [], deposits: [],
		bills: [], bill_line_items: [], checks: [], bill_payments: [],
		credits: [], refunds: [], bank_transfers: [], returned_payments: [],
		payment_applications: [], credit_applications: [], projection_audit: [],
	};

	readonly audit: QbCompiledAuditRow[] = [];
	readonly byProjection: Record<string, number> = {};
	banksCreated = 0;

	/**
	 * Record an entry that posted to the journal but produced no domain row
	 * (unclassified journal-only entry, or a projection that threw). Pushes to
	 * BOTH the returned summary AND a projection_audit row keyed by the
	 * import_batch_id — mirroring the raw pipeline's auditUnclassified so the
	 * import-batch review surfaces these exactly the same way.
	 */
	auditEntry(entryId: string, entry: ParsedEntry, classification: string, reason: string): void {
		this.audit.push({ transactionId: entry.transactionId, qbType: entry.type, postedOn: entry.postedOn, reason });
		this.rows.projection_audit.push({
			id: this.newId('paud'),
			import_batch_id: this.opts.importBatchId,
			association_id: this.opts.associationId,
			entry_id: entryId,
			classification,
			reason: `${entry.type}: ${reason}`,
			created_at: this.opts.now,
		});
	}

	/** Unknown payees collected in probe mode, keyed by lower(name). */
	readonly unresolvedVendors = new Map<string, UnresolvedVendor>();
	readonly unresolvedOwners = new Map<string, UnresolvedOwner>();

	private readonly charges: CompiledCharge[] = [];
	private readonly bills: CompiledBill[] = [];
	private readonly payments: CompiledPayment[] = [];
	/** (bank_account_id, check_number) → the one physical `checks` row every
	 *  check-method bill payment for that pair links to. Mirrors findOrCreateCheck
	 *  (checks-repo) + the mig-193 backfill: one check per bank+number, its
	 *  amount_cents = Σ linked payments, check_date = MIN(paid_on). */
	private readonly checksByKey = new Map<string, CompiledRow>();
	/** payment_id → charge ids it applied to (returned-payment reversal). */
	private readonly chargesByPayment = new Map<string, string[]>();
	private seq = 0;
	private readonly newId: (prefix: string) => string;

	/** Set when auto-correction is enabled — resolveLines maps CLEARING_TOKEN here. */
	clearingGl: QbCompileGlCode | null = null;

	private readonly res: QbCompileResolutions;

	constructor(
		res: QbCompileResolutions,
		private readonly opts: QbCompileOptions,
	) {
		// CLONE the mutable maps — ensureGl/bankIdForGl register the rows they
		// create so later lines reuse them, and writing into the CALLER's maps
		// poisons any subsequent compile: the second run would resolve to the
		// first run's client ids without emitting the rows, producing dangling
		// FK references (found the hard way in the Parkside acceptance).
		this.res = {
			...res,
			glByCode: new Map(res.glByCode),
			bankAccountIdByGlCodeId: new Map(res.bankAccountIdByGlCodeId),
		};
		this.newId = opts.newId ?? ((prefix: string) => `${prefix}_${crypto.randomUUID()}`);
	}

	// ── Line building (mirror of buildLines + ensureBankForGlCode) ─────────

	private bankIdForGl(glCodeId: string, glName: string, glCode: string): string {
		const existing = this.res.bankAccountIdByGlCodeId.get(glCodeId);
		if (existing) return existing;
		const id = this.newId('bank');
		this.rows.bank_accounts.push({
			id,
			association_id: this.opts.associationId,
			name: glName.trim() || `Bank ${glCode}`,
			kind: 'checking',
			current_balance_cents: 0,
			is_default: 0,
			active: 1,
			gl_code_id: glCodeId,
			created_at: this.opts.now,
			updated_at: this.opts.now,
		});
		this.res.bankAccountIdByGlCodeId.set(glCodeId, id);
		this.banksCreated++;
		return id;
	}

	/**
	 * Resolve an unknown GL code by CREATING the gl_codes row in the compiled
	 * set — the same thing the server pipeline's loadResolvedMaps does. A NULL
	 * gl_code_id would violate journal_lines' NOT NULL and the loader's
	 * INSERT OR IGNORE would silently drop the line, unbalancing the entry.
	 */
	private ensureGl(code: string, name: string, typeOverride?: string): QbCompileGlCode {
		const existing = this.res.glByCode.get(code);
		if (existing) return existing;
		const accountType = typeOverride ?? inferGlAccountType(code);
		const gl: QbCompileGlCode = { id: this.newId('gl'), accountType, name: name || code };
		this.rows.gl_codes.push({
			id: gl.id,
			code,
			name: gl.name,
			full_path: name ? `${code} · ${name}` : code,
			account_type: accountType,
			active: 1,
			sort_order: 0,
			import_batch_id: this.opts.importBatchId,
			created_at: this.opts.now,
			updated_at: this.opts.now,
		});
		this.res.glByCode.set(code, gl);
		return gl;
	}

	resolveLines(entry: ParsedEntry): ResolvedLine[] {
		const out: ResolvedLine[] = [];
		entry.lines.forEach((l: ParsedLine, i: number) => {
			// Mirror the raw importer's buildLines normalization so the compiled
			// rows satisfy journal_lines' CHECK (exactly one of debit/credit > 0):
			//   - QB emits zero-amount placeholder/memo lines → skip
			//   - a line should never carry BOTH debit and credit, but if it does
			//     (odd exports), net it; a net of zero is also skipped.
			let debitCents = l.debitCents;
			let creditCents = l.creditCents;
			if (debitCents === 0 && creditCents === 0) return;
			if (debitCents > 0 && creditCents > 0) {
				const net = debitCents - creditCents;
				debitCents = net > 0 ? net : 0;
				creditCents = net < 0 ? -net : 0;
				if (debitCents === 0 && creditCents === 0) return;
			}
			let gl = l.accountCode === CLEARING_TOKEN
				? this.clearingGl
				: this.res.glByCode.get(l.accountCode) ?? null;
			if (!gl && l.accountCode) {
				// Unknown code WITH a code string → auto-create (server parity).
				gl = this.ensureGl(l.accountCode, l.accountName);
			}
			if (!gl) {
				// Code-less line (correction offsets, odd exports): unassigned
				// income/expense fallback, auto-created when the association has
				// none configured.
				const fallbackId = creditCents > 0 ? this.res.fallbackIncomeGlId : this.res.fallbackExpenseGlId;
				gl = fallbackId
					? { id: fallbackId, accountType: creditCents > 0 ? 'INC' : 'EXP', name: l.accountName }
					: this.ensureGl(
						creditCents > 0 ? 'UNASSIGNED-INC' : 'UNASSIGNED-EXP',
						creditCents > 0 ? 'Unassigned Income' : 'Unassigned Expense',
						creditCents > 0 ? 'INC' : 'EXP',
					);
			}
			const isBank = gl != null && BANK_TYPES.has(gl.accountType);
			const isAr = gl != null && AR_TYPES.has(gl.accountType);
			out.push({
				glCodeId: gl?.id ?? null,
				accountType: gl?.accountType ?? null,
				accountId: isAr ? this.resolveArAccountId(l.name) : null,
				bankAccountId: isBank && gl ? this.bankIdForGl(gl.id, gl.name, l.accountCode) : null,
				debitCents,
				creditCents,
				memo: l.memo,
				sortOrder: i,
				qbName: l.name,
			});
		});
		return out;
	}

	/**
	 * Resolve an A/R line's QB "Name" (the account display name — e.g.
	 * "1 - ` Mittiga", "742PS - J", "105 - Strauss / Dolan") to an owner
	 * accounts.id. Order:
	 *   1. `resolveAccountId` (the shared matchSection convention injected by the
	 *      client: external_name exact → account-number prefix → owner-name
	 *      tie-break). Authoritative in production.
	 *   2. Fallback for pure/unit contexts: the display-name account-number prefix
	 *      ("1 - ` Mittiga" → "1", "742PS - J" → "742PS") against
	 *      `accountIdByShortcode` (which is keyed by account_number).
	 *   3. Legacy shortcode extraction (\d+[A-Z]{2,3}) for names that carry a
	 *      shortcode token but no " - " separator.
	 * Returns null when no owner matches — the projection then audits the entry
	 * instead of attaching it to the wrong account.
	 */
	private resolveArAccountId(name: string | null | undefined): string | null {
		if (name == null) return null;
		const viaShared = this.res.resolveAccountId?.(name);
		if (viaShared) return viaShared;
		const prefix = parseCustomerName(name).accountNumberPrefix;
		if (prefix) {
			const byPrefix = this.res.accountIdByShortcode.get(prefix);
			if (byPrefix) return byPrefix;
		}
		const shortcode = extractShortcode(name);
		return shortcode ? (this.res.accountIdByShortcode.get(shortcode) ?? null) : null;
	}

	// ── Posting (mirror of postLedgerEntriesBulk column shapes) ────────────

	postEntry(entry: ParsedEntry, resolved: ResolvedLine[]): string {
		const entryId = this.newId('je');
		this.rows.journal_entries.push({
			id: entryId,
			association_id: this.opts.associationId,
			posted_on: entry.postedOn,
			memo: composeMemo(entry),
			source: 'import',
			source_ref: `${this.opts.associationId}:${entry.transactionId}`,
			reverses_entry_id: null,
			created_by: this.opts.createdBy ?? null,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			import_batch_id: this.opts.importBatchId,
			// Mirror the raw workflow's post-projection sweep
			// (project-from-journal stamps cleared_at = posted_on on every
			// imported entry when markCleared) so a bank-touching JOURNAL-ONLY
			// entry counts as cleared and the register's cleared balance ties to
			// QuickBooks. Domain-backed rows ignore this in favor of their own
			// cleared_at column.
			cleared_at: this.opts.markCleared ? entry.postedOn : null,
		});
		for (const ln of resolved) {
			this.rows.journal_lines.push({
				id: this.newId('jl'),
				entry_id: entryId,
				sort_order: ln.sortOrder,
				gl_code_id: ln.glCodeId,
				fund_id: this.res.defaultFundId,
				debit_cents: ln.debitCents,
				credit_cents: ln.creditCents,
				memo: ln.memo,
				created_at: this.opts.now,
				account_id: ln.accountId,
				bank_account_id: ln.bankAccountId,
				vendor_id: null,
			});
		}
		return entryId;
	}

	// ── Classification (verbatim port of classifyEntry) ────────────────────

	classify(entry: ParsedEntry, resolved: ResolvedLine[]): { type: ProjectionType | 'unclassified'; reason?: string } {
		const shape = shapeFromLines(resolved);

		if (has(shape.debitsByType, BANK_TYPES) && has(shape.creditsByType, BANK_TYPES)
			&& !has(shape.debitsByType, AR_TYPES) && !has(shape.creditsByType, AR_TYPES)
			&& !has(shape.debitsByType, AP_TYPES) && !has(shape.creditsByType, AP_TYPES)) {
			return { type: 'bank_transfer' };
		}
		if (has(shape.debitsByType, AR_TYPES) && has(shape.creditsByType, BANK_TYPES)) {
			return { type: 'returned_payment' };
		}
		const direct = QB_TYPE_TO_PROJECTION[entry.type];
		if (direct === 'bill_payment') {
			const hasApDebit = has(shape.debitsByType, AP_TYPES);
			const hasVendor = resolved.some((l) => l.debitCents > 0
				&& !BANK_TYPES.has(l.accountType ?? '')
				&& !!(l.qbName && l.qbName.trim()));
			if (!hasApDebit && !hasVendor) {
				return {
					type: 'unclassified',
					reason: `QB ${entry.type} with no AP debit and no vendor name — journal-only entry, shape ${shape.summary}`,
				};
			}
		}
		if (direct) {
			if (validateShape(direct, shape)) return { type: direct };
			return { type: 'unclassified', reason: `QB type ${entry.type} but line shape ${shape.summary} does not match` };
		}
		const inferred = inferFromShape(shape);
		if (inferred) return { type: inferred };
		return { type: 'unclassified', reason: `Unrecognized QB type "${entry.type}" with shape ${shape.summary}` };
	}

	// ── Bank-line resolution (mirror of resolveBankLine) ───────────────────

	private bankLine(resolved: ResolvedLine[], side: 'debit' | 'credit'): { line: ResolvedLine; bankAccountId: string } | null {
		const amt = (l: ResolvedLine) => (side === 'debit' ? l.debitCents : l.creditCents);
		const candidates = resolved
			.filter((l) => BANK_TYPES.has(l.accountType ?? '') && amt(l) > 0)
			.sort((a, b) => amt(b) - amt(a));
		for (const line of candidates) {
			if (line.bankAccountId) return { line, bankAccountId: line.bankAccountId };
		}
		return null;
	}

	// ── Per-type projection (pure ports) ───────────────────────────────────

	project(type: ProjectionType, entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		switch (type) {
			case 'charge': return this.projectCharge(entry, resolved, entryId);
			case 'payment': return this.projectPayment(entry, resolved, entryId);
			case 'bill': return this.projectBill(entry, resolved, entryId);
			case 'bill_payment': return this.projectBillPayment(entry, resolved, entryId);
			case 'credit': return this.projectCredit(entry, resolved, entryId);
			case 'refund': return this.projectRefund(entry, resolved, entryId);
			case 'deposit': return this.projectDeposit(entry, resolved, entryId);
			case 'bank_transfer': return this.projectBankTransfer(entry, resolved, entryId);
			case 'returned_payment': return this.projectReturnedPayment(entry, resolved, entryId);
		}
	}

	private projectCharge(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const arLine = resolved.find((l) => AR_TYPES.has(l.accountType ?? '') && l.debitCents > 0);
		if (!arLine) throw new Error('charge: no AR debit line with account_id');
		if (!arLine.accountId) this.failUnresolvedOwner(arLine.qbName, entry.type, arLine.debitCents, 'charge: no AR debit line with account_id');
		const totalAr = resolved
			.filter((l) => AR_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.debitCents - l.creditCents, 0);
		if (totalAr <= 0) throw new Error('charge: net AR debit must be > 0');

		const incomeLine = resolved.find((l) => l.creditCents > 0 && !AR_TYPES.has(l.accountType ?? '') && l.glCodeId);
		const glCodeId = incomeLine?.glCodeId ?? arLine.glCodeId ?? null;

		const id = this.newId('chg');
		this.rows.charges.push({
			id,
			account_id: arLine.accountId,
			posted_on: entry.postedOn,
			// Imported charges carry no explicit due date — default it to the
			// posted date so they age + qualify for late fees like real charges
			// (otherwise NULL due_on excludes them from postLateFees entirely).
			due_on: entry.postedOn,
			amount_cents: totalAr,
			paid_cents: 0,	// finalized after FIFO
			category: 'general',
			memo: composeMemo(entry),
			source: 'manual',
			source_ref: `${this.opts.associationId}:${entry.transactionId}`,
			source_entry_id: entryId,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			gl_code_id: glCodeId,
		});
		this.charges.push({ id, accountId: arLine.accountId, postedOn: entry.postedOn, amountCents: totalAr, paidCents: 0, seq: this.seq++ });
	}

	private projectPayment(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const arLine = resolved.find((l) => AR_TYPES.has(l.accountType ?? '') && l.creditCents > 0);
		if (!arLine) throw new Error('payment: no AR credit line with account_id');
		if (!arLine.accountId) this.failUnresolvedOwner(arLine.qbName, entry.type, arLine.creditCents, 'payment: no AR credit line with account_id');
		const totalAr = resolved
			.filter((l) => AR_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.creditCents - l.debitCents, 0);
		if (totalAr <= 0) throw new Error('payment: net AR credit must be > 0');

		const resolvedBank = this.bankLine(resolved, 'debit');
		const bankAccountId = resolvedBank?.bankAccountId ?? null;

		const id = this.newId('pay');
		const memo = composeMemo(entry);
		const clearedAt = this.opts.markCleared && bankAccountId ? entry.postedOn : null;
		const row: CompiledRow = {
			id,
			account_id: arLine.accountId,
			received_on: entry.postedOn,
			amount_cents: totalAr,
			method: entry.type === 'Payment' ? 'check' : 'other',
			reference: entry.num,
			memo,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			bank_account_id: bankAccountId,
			cleared_at: clearedAt,
			source_entry_id: entryId,
			deposit_id: null,
			returned_at: null,
		};
		this.rows.payments.push(row);

		if (bankAccountId) {
			const depId = this.newId('dep');
			this.rows.deposits.push({
				id: depId,
				association_id: this.opts.associationId,
				bank_account_id: bankAccountId,
				deposited_on: entry.postedOn,
				memo: `Auto-grouped from import: ${memo}`,
				total_cents: totalAr,
				created_at: this.opts.now,
				updated_at: this.opts.now,
				source_entry_id: entryId,
			});
			row.deposit_id = depId;
		}

		const payment: CompiledPayment = {
			id, accountId: arLine.accountId, receivedOn: entry.postedOn,
			amountCents: totalAr, appliedCents: 0, returnedAt: null,
			reference: entry.num, row,
		};
		this.payments.push(payment);
		this.applyPaymentFifo(payment);
	}

	/** Mirror of autoApplyPaymentFifo over the in-memory charge set. */
	private applyPaymentFifo(payment: CompiledPayment): void {
		const open = this.charges
			.filter((c) => c.accountId === payment.accountId && c.amountCents > c.paidCents)
			.sort((a, b) => a.postedOn - b.postedOn || a.seq - b.seq);
		let remaining = payment.amountCents - payment.appliedCents;
		for (const c of open) {
			if (remaining <= 0) break;
			const owed = c.amountCents - c.paidCents;
			const apply = Math.min(remaining, owed);
			this.rows.payment_applications.push({
				id: this.newId('papp'),
				payment_id: payment.id,
				charge_id: c.id,
				amount_cents_applied: apply,
				created_at: this.opts.now,
			});
			c.paidCents += apply;
			remaining -= apply;
			payment.appliedCents += apply;
			const applied = this.chargesByPayment.get(payment.id) ?? [];
			applied.push(c.id);
			this.chargesByPayment.set(payment.id, applied);
		}
	}

	private projectBill(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const apLine = resolved.find((l) => AP_TYPES.has(l.accountType ?? '') && l.creditCents > 0);
		if (!apLine) throw new Error('bill: no AP credit line');
		const totalAp = resolved
			.filter((l) => AP_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.creditCents - l.debitCents, 0);
		if (totalAp <= 0) throw new Error('bill: net AP credit must be > 0');

		const vendorId = this.vendorFor(apLine.qbName);
		if (!vendorId) this.failUnresolvedVendor(apLine.qbName, entry.type, totalAp, 'bill');

		this.emitBill(entry, resolved, entryId, vendorId, totalAp, (ln) => ln.debitCents > 0 && !AP_TYPES.has(ln.accountType ?? ''));
	}

	private emitBill(
		entry: ParsedEntry, resolved: ResolvedLine[], entryId: string | null,
		vendorId: string, totalCents: number,
		lineFilter: (ln: ResolvedLine) => boolean,
	): CompiledBill {
		const id = this.newId('bill');
		const row: CompiledRow = {
			id,
			association_id: this.opts.associationId,
			vendor_id: vendorId,
			bill_number: entry.num,
			issued_on: entry.postedOn,
			due_on: entry.postedOn,
			total_cents: totalCents,
			paid_cents: 0,	// finalized after allocation
			status: 'open',
			memo: composeMemo(entry),
			source: 'import',
			source_ref: `${this.opts.associationId}:${entry.transactionId}`,
			source_entry_id: entryId,
			is_imported_stub: 1,
			// Stub bills carry NO source_entry_id, so the loader's count
			// verification (and batch revert) key on import_batch_id instead.
			import_batch_id: this.opts.importBatchId,
			created_at: this.opts.now,
			updated_at: this.opts.now,
		};
		this.rows.bills.push(row);
		let sort = 0;
		for (const ln of resolved) {
			if (!lineFilter(ln)) continue;
			this.rows.bill_line_items.push({
				id: this.newId('bli'),
				bill_id: id,
				sort_order: sort++,
				description: ln.memo ?? '',
				gl_code_id: ln.glCodeId,
				amount_cents: ln.debitCents,
				memo: ln.memo,
				created_at: this.opts.now,
				updated_at: this.opts.now,
			});
		}
		const bill: CompiledBill = { id, vendorId, issuedOn: entry.postedOn, totalCents, paidCents: 0, seq: this.seq++, row };
		this.bills.push(bill);
		return bill;
	}

	private vendorFor(qbName: string | null): string | null {
		if (!qbName) return null;
		return this.res.vendorIdByLowerName.get(qbName.trim().toLowerCase()) ?? null;
	}

	/**
	 * Find-or-create the ONE physical check for (bank_account_id, check_number)
	 * and return its row so the caller can stamp bill_payments.check_id + fold in
	 * this payment's amount. Pure-compiler port of findOrCreateCheck (checks-repo):
	 * the same vendor reuses the check (a check pays many bills); the first vendor
	 * seen wins. amount_cents accumulates across linked payments and check_date is
	 * the earliest paid_on — matching the mig-193 backfill's SUM/MIN grouping.
	 */
	private getOrCreateCheck(bankAccountId: string, checkNumber: string, vendorId: string | null, paidOn: number, memo: string | null): CompiledRow {
		const key = `${bankAccountId} ${checkNumber}`;
		const existing = this.checksByKey.get(key);
		if (existing) {
			if (paidOn < Number(existing.check_date)) existing.check_date = paidOn;
			return existing;
		}
		const row: CompiledRow = {
			id: this.newId('chk'),
			bank_account_id: bankAccountId,
			association_id: this.opts.associationId,
			check_number: checkNumber,
			vendor_id: vendorId,
			check_date: paidOn,
			memo,
			amount_cents: 0,	// accumulated as payments link
			created_by: this.opts.createdBy ?? null,
			created_at: this.opts.now,
			updated_at: this.opts.now,
		};
		this.rows.checks.push(row);
		this.checksByKey.set(key, row);
		return row;
	}

	/**
	 * Record a payee that should resolve to a vendor but doesn't. Tallies one
	 * row per distinct lower(name) for the UI's create-or-map prompt. Always
	 * throws afterward so the caller's catch routes the entry to journal-only
	 * (its partial rows are discarded — the probe compile only needs the names).
	 */
	private failUnresolvedVendor(qbName: string | null, qbType: string, cents: number, context: string): never {
		const name = (qbName ?? '').trim();
		if (this.opts.collectUnresolvedVendors && name) {
			const key = name.toLowerCase();
			const existing = this.unresolvedVendors.get(key);
			if (existing) existing.count++;
			else this.unresolvedVendors.set(key, { name, qbType, sampleCents: cents, count: 1 });
		}
		throw new Error(`${context}: vendor not found for QB Name "${name}"`);
	}

	/**
	 * An A/R entry's owner QB "Name" didn't resolve to an account. In probe mode
	 * record one row per distinct lower(name) (with a running count) for the
	 * modal's blocking review. Always throws `message` afterward so the caller's
	 * catch audits the entry as before (its partial rows are discarded).
	 */
	private failUnresolvedOwner(qbName: string | null | undefined, qbType: string, cents: number, message: string): never {
		const name = (qbName ?? '').trim();
		if (this.opts.collectUnresolvedOwners && name) {
			const key = name.toLowerCase();
			const existing = this.unresolvedOwners.get(key);
			if (existing) existing.count++;
			else this.unresolvedOwners.set(key, { name, qbType, sampleCents: cents, count: 1 });
		}
		throw new Error(message);
	}

	private projectBillPayment(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const resolvedBank = this.bankLine(resolved, 'credit');
		if (!resolvedBank) throw new Error('bill_payment: no resolvable bank credit line');
		const bankAccountId = resolvedBank.bankAccountId;

		const apDebit = resolved.find((l) => AP_TYPES.has(l.accountType ?? '') && l.debitCents > 0);
		const totalAp = apDebit
			? resolved
				.filter((l) => AP_TYPES.has(l.accountType ?? ''))
				.reduce((s, l) => s + l.debitCents - l.creditCents, 0)
			: resolved
				.filter((l) => l.debitCents > 0 && !BANK_TYPES.has(l.accountType ?? ''))
				.reduce((s, l) => s + l.debitCents - l.creditCents, 0);
		if (totalAp <= 0) throw new Error('bill_payment: net debit must be > 0');

		let vendorLine: ResolvedLine | undefined = apDebit;
		if (!vendorLine) {
			const nonBankDebits = resolved.filter((l) => l.debitCents > 0 && !BANK_TYPES.has(l.accountType ?? ''));
			vendorLine = nonBankDebits.find((l) => !!l.qbName) ?? nonBankDebits[0];
			if (!vendorLine) throw new Error('bill_payment: no debit line to pay');
		}
		const vendorId = this.vendorFor(vendorLine.qbName);
		if (!vendorId) this.failUnresolvedVendor(vendorLine.qbName, entry.type, totalAp, 'bill_payment');

		// Open bills for this vendor, oldest-first — same query the server runs,
		// over the in-memory compiled set.
		const openBills = this.bills
			.filter((b) => b.vendorId === vendorId && b.totalCents > b.paidCents)
			.sort((a, b) => a.issuedOn - b.issuedOn || a.seq - b.seq);

		let allocations: Array<{ bill: CompiledBill; amountCents: number }>;
		if (openBills.length === 0) {
			// Stamp the stub bill with THIS entry's id (parity with the raw
			// synthesizeStubBill) so revert/reset reach it through source_entry_id;
			// without it the stub bill + its line item orphan after a revert.
			const stub = this.emitBill(entry, resolved, entryId, vendorId, totalAp,
				(ln) => ln.debitCents > 0 && !BANK_TYPES.has(ln.accountType ?? '') && !AP_TYPES.has(ln.accountType ?? ''));
			allocations = [{ bill: stub, amountCents: totalAp }];
		} else {
			allocations = allocateBillPayment(totalAp, openBills);
			const allocated = allocations.reduce((s, a) => s + a.amountCents, 0);
			if (allocated < totalAp && allocations.length > 0) {
				allocations[allocations.length - 1].amountCents += totalAp - allocated;
			}
		}

		const clearedAt = this.opts.markCleared ? entry.postedOn : null;
		const memo = composeMemo(entry);
		const checkNumber = toCheckNumber(entry.num);
		// Group check-method payments under one physical check (parity with
		// bills-bulk.ts + findOrCreateCheck): only when there's a resolvable bank
		// and a numeric check number, matching the backfill's GLOB '[0-9]*' filter.
		const check = checkNumber ? this.getOrCreateCheck(bankAccountId, checkNumber, vendorId, entry.postedOn, memo) : null;
		let first = true;
		for (const a of allocations) {
			this.rows.bill_payments.push({
				id: this.newId('bpay'),
				bill_id: a.bill.id,
				bank_account_id: bankAccountId,
				paid_on: entry.postedOn,
				amount_cents: a.amountCents,
				method: 'check',
				check_number: checkNumber,
				check_id: check?.id ?? null,
				reference: entry.num,
				memo,
				cleared_at: clearedAt,
				created_at: this.opts.now,
				updated_at: this.opts.now,
				source_entry_id: first ? entryId : null,
			});
			if (check) check.amount_cents = Number(check.amount_cents) + a.amountCents;
			a.bill.paidCents += a.amountCents;
			first = false;
		}
	}

	private projectCredit(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const arLine = resolved.find((l) => AR_TYPES.has(l.accountType ?? '') && l.creditCents > 0);
		if (!arLine) throw new Error('credit: no AR credit line with account_id');
		if (!arLine.accountId) this.failUnresolvedOwner(arLine.qbName, entry.type, arLine.creditCents, 'credit: no AR credit line with account_id');
		const totalAr = resolved
			.filter((l) => AR_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.creditCents - l.debitCents, 0);
		if (totalAr <= 0) throw new Error('credit: net AR credit must be > 0');

		const offsetLine = resolved.find((l) => l.debitCents > 0 && !AR_TYPES.has(l.accountType ?? ''));
		const id = this.newId('cr');
		this.rows.credits.push({
			id,
			account_id: arLine.accountId,
			issued_on: entry.postedOn,
			amount_cents: totalAr,
			memo: composeMemo(entry),
			created_at: this.opts.now,
			updated_at: this.opts.now,
			gl_code_id: offsetLine?.glCodeId ?? arLine.glCodeId ?? null,
			credit_number: entry.num,
			source_entry_id: entryId,
		});

		// Mirror autoApplyCreditFifo: credits settle open charges oldest-first.
		const open = this.charges
			.filter((c) => c.accountId === arLine.accountId && c.amountCents > c.paidCents)
			.sort((a, b) => a.postedOn - b.postedOn || a.seq - b.seq);
		let remaining = totalAr;
		for (const c of open) {
			if (remaining <= 0) break;
			const apply = Math.min(remaining, c.amountCents - c.paidCents);
			this.rows.credit_applications.push({
				id: this.newId('capp'),
				credit_id: id,
				charge_id: c.id,
				amount_cents_applied: apply,
				created_at: this.opts.now,
			});
			c.paidCents += apply;
			remaining -= apply;
		}
	}

	private projectRefund(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const arLine = resolved.find((l) => AR_TYPES.has(l.accountType ?? '') && l.debitCents > 0);
		if (!arLine) throw new Error('refund: no AR debit line with account_id');
		if (!arLine.accountId) this.failUnresolvedOwner(arLine.qbName, entry.type, arLine.debitCents, 'refund: no AR debit line with account_id');
		const total = resolved
			.filter((l) => AR_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.debitCents - l.creditCents, 0);
		if (total <= 0) throw new Error('refund: net AR debit must be > 0');

		const resolvedBank = this.bankLine(resolved, 'credit');
		this.rows.refunds.push({
			id: this.newId('ref'),
			account_id: arLine.accountId,
			issued_on: entry.postedOn,
			amount_cents: total,
			method: 'other',
			memo: composeMemo(entry),
			created_at: this.opts.now,
			updated_at: this.opts.now,
			gl_code_id: resolvedBank?.line.glCodeId ?? null,
			source_entry_id: entryId,
		});
	}

	private projectDeposit(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const resolvedBank = this.bankLine(resolved, 'debit');
		if (!resolvedBank) throw new Error('deposit: no bank debit line with bank_account_id');
		const total = resolved
			.filter((l) => BANK_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.debitCents - l.creditCents, 0);
		if (total <= 0) throw new Error('deposit: net bank debit must be > 0');

		this.rows.deposits.push({
			id: this.newId('dep'),
			association_id: this.opts.associationId,
			bank_account_id: resolvedBank.bankAccountId,
			deposited_on: entry.postedOn,
			memo: composeMemo(entry),
			total_cents: total,
			cleared_at: this.opts.markCleared ? entry.postedOn : null,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			source_entry_id: entryId,
		});
	}

	private projectBankTransfer(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const to = this.bankLine(resolved, 'debit');
		const from = this.bankLine(resolved, 'credit');
		if (!to || !from) throw new Error('bank_transfer: missing from/to bank_account_id');
		const amount = Math.max(to.line.debitCents, from.line.creditCents);
		if (amount <= 0) throw new Error('bank_transfer: amount must be > 0');

		const clearedAt = this.opts.markCleared ? entry.postedOn : null;
		this.rows.bank_transfers.push({
			id: this.newId('bt'),
			association_id: this.opts.associationId,
			from_bank_id: from.bankAccountId,
			to_bank_id: to.bankAccountId,
			transferred_on: entry.postedOn,
			amount_cents: amount,
			memo: composeMemo(entry),
			reference: entry.num,
			from_cleared_at: clearedAt,
			to_cleared_at: clearedAt,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			source_entry_id: entryId,
		});
	}

	private projectReturnedPayment(entry: ParsedEntry, resolved: ResolvedLine[], entryId: string): void {
		const arLine = resolved.find((l) => AR_TYPES.has(l.accountType ?? '') && l.debitCents > 0);
		if (!arLine) throw new Error('returned_payment: no AR debit line with account_id');
		if (!arLine.accountId) this.failUnresolvedOwner(arLine.qbName, entry.type, arLine.debitCents, 'returned_payment: no AR debit line with account_id');
		const total = resolved
			.filter((l) => AR_TYPES.has(l.accountType ?? ''))
			.reduce((s, l) => s + l.debitCents - l.creditCents, 0);
		if (total <= 0) throw new Error('returned_payment: net AR debit must be > 0');

		const resolvedBank = this.bankLine(resolved, 'credit');
		if (!resolvedBank) throw new Error('returned_payment: no resolvable bank credit line');

		// Mirror findOriginalPaymentForReturn over the compiled payment set.
		const matchMemo = arLine.memo ?? composeMemo(entry);
		const matched = this.findOriginalPayment(arLine.accountId, total, matchMemo);

		this.rows.returned_payments.push({
			id: this.newId('rpay'),
			association_id: this.opts.associationId,
			payment_id: matched?.id ?? null,
			account_id: arLine.accountId,
			bank_account_id: resolvedBank.bankAccountId,
			returned_on: entry.postedOn,
			amount_cents: total,
			reason: 'imported',
			source_entry_id: entryId,
			cleared_at: this.opts.markCleared ? entry.postedOn : null,
			memo: composeMemo(entry),
			created_at: this.opts.now,
			updated_at: this.opts.now,
		});

		// Re-establish the receivable as a charge-back charge (the AR side of the
		// bounce). The bulk-loaded balance derives from charges − payments −
		// credits + refunds, so this charge is what makes the customer owe the
		// returned funds again — the returned_payments row alone feeds only the
		// bank register. We do NOT reverse the matched original payment: QuickBooks
		// keeps it on the books and re-bills the bounce as a new invoice, and
		// relying on a confident match to re-establish AR silently dropped every
		// CHECK NSF (the matcher only fires on Zego-style memos). `matched` is kept
		// only as the returned_payments link for NSF reporting. Added to the FIFO
		// charge set so a later payment in the same import can settle it.
		const chgId = this.newId('chg');
		this.rows.charges.push({
			id: chgId,
			account_id: arLine.accountId,
			posted_on: entry.postedOn,
			due_on: entry.postedOn,	// default due date = posted date (see projectCharge)
			amount_cents: total,
			paid_cents: 0,
			category: 'returned_payment',
			memo: composeMemo(entry),
			source: 'manual',
			source_ref: `${this.opts.associationId}:${entry.transactionId}`,
			source_entry_id: entryId,
			created_at: this.opts.now,
			updated_at: this.opts.now,
			gl_code_id: arLine.glCodeId ?? null,
		});
		this.charges.push({ id: chgId, accountId: arLine.accountId, postedOn: entry.postedOn, amountCents: total, paidCents: 0, seq: this.seq++ });
	}

	private findOriginalPayment(accountId: string, amountCents: number, memo: string | null): CompiledPayment | null {
		if (!memo) return null;
		const m = memo.match(/payment of (\d{1,2})\/(\d{1,2})\/(\d{2,4})/i);
		if (!m) return null;
		const month = Number(m[1]);
		const day = Number(m[2]);
		let year = Number(m[3]);
		if (year < 100) year += 2000;
		const target = Date.UTC(year, month - 1, day);
		const WINDOW = 3 * 24 * 60 * 60 * 1000;
		let candidates = this.payments.filter((p) =>
			p.accountId === accountId && p.amountCents === amountCents
			&& p.returnedAt == null
			&& p.receivedOn >= target - WINDOW && p.receivedOn <= target + WINDOW);
		const zego = candidates.filter((p) => (p.reference ?? '').toLowerCase() === 'zego');
		if (zego.length > 0) candidates = zego;
		return candidates.length === 1 ? candidates[0] : null;
	}

	// ── Finalize ───────────────────────────────────────────────────────────

	/**
	 * Forward sweep for prepayments: a payment dated BEFORE its future charges
	 * leaves an unapplied remainder during the date-ordered pass. Re-apply
	 * every free remainder oldest-payment-first — the in-memory equivalent of
	 * the server's post-drain reapply sweep.
	 */
	finalizeApplications(): void {
		const withRemainder = this.payments
			.filter((p) => p.returnedAt == null && p.appliedCents < p.amountCents)
			.sort((a, b) => a.receivedOn - b.receivedOn);
		for (const p of withRemainder) this.applyPaymentFifo(p);
	}

	/** Stamp final paid_cents/status onto the compiled charge + bill rows. */
	finalizeRows(): void {
		const chargeById = new Map(this.charges.map((c) => [c.id, c]));
		for (const row of this.rows.charges) {
			const c = chargeById.get(row.id as string);
			if (c) row.paid_cents = c.paidCents;
		}
		for (const b of this.bills) {
			b.row.paid_cents = b.paidCents;
			b.row.status = b.paidCents >= b.totalCents ? 'paid' : b.paidCents > 0 ? 'partial' : 'open';
		}
	}

	bankEndingCents(): Record<string, number> {
		const out: Record<string, number> = {};
		for (const ln of this.rows.journal_lines) {
			const bankId = ln.bank_account_id as string | null;
			if (!bankId) continue;
			out[bankId] = (out[bankId] ?? 0) + Number(ln.debit_cents) - Number(ln.credit_cents);
		}
		return out;
	}
}

// ── Pure helpers (verbatim ports) ───────────────────────────────────────────

function composeMemo(p: ParsedEntry): string {
	const parts: string[] = [p.type];
	if (p.num) parts.push(`#${p.num}`);
	const firstMemo = p.lines.find((l) => l.memo)?.memo ?? null;
	if (firstMemo) parts.push(firstMemo);
	return parts.join(' ');
}

function toCheckNumber(qbNum: string | null): string | null {
	if (!qbNum) return null;
	const trimmed = qbNum.trim();
	return /^\d+$/.test(trimmed) ? trimmed : null;
}

/**
 * Account-type inference for unknown GL codes — verbatim port of the server
 * importer's convention (QB code ranges, ProCondo-style charts).
 */
function inferGlAccountType(code: string): string {
	const n = parseFloat(code);
	if (!Number.isFinite(n)) return 'OTHER';
	if (n >= 1000 && n < 2000) return 'OCASSET';
	if (n >= 2000 && n < 2800) return 'AP';
	if (n >= 2800 && n < 3000) return 'EQUITY';
	if (n >= 3000 && n < 4000) return 'INC';
	if (n >= 4000 && n < 8000) return 'EXP';
	if (n >= 8000 && n < 9000) return 'OEXP';
	if (n >= 9000) return 'OINC';
	return 'OTHER';
}

interface ShapeSummary {
	debitsByType: Record<string, number>;
	creditsByType: Record<string, number>;
	summary: string;
}

function shapeFromLines(lines: ResolvedLine[]): ShapeSummary {
	const debitsByType: Record<string, number> = {};
	const creditsByType: Record<string, number> = {};
	for (const ln of lines) {
		const t = ln.accountType ?? '?';
		if (ln.debitCents > 0) debitsByType[t] = (debitsByType[t] ?? 0) + ln.debitCents;
		if (ln.creditCents > 0) creditsByType[t] = (creditsByType[t] ?? 0) + ln.creditCents;
	}
	const dParts = Object.entries(debitsByType).map(([t, v]) => `${t}:${v}`).join(',');
	const cParts = Object.entries(creditsByType).map(([t, v]) => `${t}:${v}`).join(',');
	return { debitsByType, creditsByType, summary: `D{${dParts}}/C{${cParts}}` };
}

function has(map: Record<string, number>, tags: Set<string>): boolean {
	for (const t of Object.keys(map)) if (tags.has(t) && map[t] > 0) return true;
	return false;
}

function validateShape(type: ProjectionType, shape: ShapeSummary): boolean {
	const { debitsByType: D, creditsByType: C } = shape;
	switch (type) {
		case 'charge':       return has(D, AR_TYPES);
		case 'payment':      return has(C, AR_TYPES);
		case 'bill':         return has(C, AP_TYPES);
		case 'bill_payment': return has(D, AP_TYPES) || has(C, BANK_TYPES);
		case 'credit':       return has(C, AR_TYPES);
		case 'refund':       return has(D, AR_TYPES) && has(C, BANK_TYPES);
		case 'deposit':      return has(D, BANK_TYPES);
		case 'bank_transfer': return has(D, BANK_TYPES) && has(C, BANK_TYPES);
		case 'returned_payment': return has(D, AR_TYPES) && has(C, BANK_TYPES);
	}
}

function inferFromShape(shape: ShapeSummary): ProjectionType | null {
	const { debitsByType: D, creditsByType: C } = shape;
	if (has(D, BANK_TYPES) && has(C, BANK_TYPES)) return 'bank_transfer';
	if (has(D, AP_TYPES) && has(C, BANK_TYPES)) return 'bill_payment';
	if (has(C, AP_TYPES)) return 'bill';
	if (has(D, AR_TYPES) && has(C, BANK_TYPES)) return 'refund';
	if (has(D, AR_TYPES)) return 'charge';
	if (has(C, AR_TYPES)) return 'payment';
	if (has(D, BANK_TYPES)) return 'deposit';
	return null;
}

/** Pure port of allocatePayment (exact-amount match first, then capped FIFO). */
function allocateBillPayment(
	payAmtCents: number,
	openBills: CompiledBill[],
): Array<{ bill: CompiledBill; amountCents: number }> {
	if (payAmtCents <= 0) return [];
	const exact = openBills.find((b) => b.totalCents - b.paidCents === payAmtCents);
	if (exact) return [{ bill: exact, amountCents: payAmtCents }];
	const allocations: Array<{ bill: CompiledBill; amountCents: number }> = [];
	let remaining = payAmtCents;
	for (const b of openBills) {
		if (remaining <= 0) break;
		const billRemaining = b.totalCents - b.paidCents;
		if (billRemaining <= 0) continue;
		const apply = Math.min(remaining, billRemaining);
		allocations.push({ bill: b, amountCents: apply });
		remaining -= apply;
	}
	return allocations;
}

/** Numeric-aware Trans # comparison so '99' sorts before '100'. */
const compareTransId = (a: string, b: string): number => {
	const na = Number(a);
	const nb = Number(b);
	if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
	return a < b ? -1 : a > b ? 1 : 0;
};

// ────────────────────────────────────────────────────────────────────────────
// Auto-correction (Phase 6) — synthesize journal entries for detail rows the
// journal export is missing, mirroring what the old detail-only importers
// produced (charges/payments without a bank leg → undeposited).
// ────────────────────────────────────────────────────────────────────────────

const correctionQbType: Record<string, string> = {
	'Invoice': 'Invoice',
	'Payment': 'Payment',
	'Credit Memo': 'Credit Memo',
	'Bill': 'Bill',
	'Bill Pmt -Check': 'Bill Pmt -Check',
};

/**
 * Build a synthetic ParsedEntry for one missing detail row. The known side
 * uses the report's own control account (AR/AP); the unknown side falls to
 * the unassigned-income/expense buckets (Invoices/Bills — visible P&L
 * bucket) or the clearing GL (Payments/Bill Pmts — balance-sheet only, so
 * the P&L is never polluted by a synthesized cash leg).
 */
function buildCorrectionEntry(kind: 'ar' | 'ap', row: QbDetailRow): ParsedEntry | null {
	const qbType = correctionQbType[row.type];
	if (!qbType || !row.transId || row.dateMs == null) return null;
	const cents = Math.round(Math.abs(row.amount) * 100);
	if (cents === 0) return null;
	const control = parseAccountCell(row.account ?? '');
	if (!control.code) return null;
	const memo = `[auto-correction] ${row.memo ?? `${row.type} missing from journal export`}`;
	const mk = (accountCode: string, accountName: string, debit: number, credit: number): ParsedLine =>
		({ name: row.partyName, memo, accountCode, accountName, debitCents: debit, creditCents: credit });

	let lines: ParsedLine[];
	if (kind === 'ar') {
		switch (row.type) {
			case 'Invoice':
				lines = [mk(control.code, control.name, cents, 0), mk('', 'Auto-correction income', 0, cents)];
				break;
			case 'Payment':
				lines = [mk(CLEARING_TOKEN, 'Auto-correction clearing', cents, 0), mk(control.code, control.name, 0, cents)];
				break;
			case 'Credit Memo':
				lines = [mk(CLEARING_TOKEN, 'Auto-correction clearing', cents, 0), mk(control.code, control.name, 0, cents)];
				break;
			default:
				return null;
		}
	} else {
		switch (row.type) {
			case 'Bill':
				lines = [mk('', 'Auto-correction expense', cents, 0), mk(control.code, control.name, 0, cents)];
				break;
			case 'Bill Pmt -Check':
				lines = [mk(control.code, control.name, cents, 0), mk(CLEARING_TOKEN, 'Auto-correction clearing', 0, cents)];
				break;
			default:
				return null;
		}
	}
	return { transactionId: row.transId, type: qbType, postedOn: row.dateMs, num: row.num ?? null, lines };
}

// ────────────────────────────────────────────────────────────────────────────
// Entry point
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compile deduped journal entries into the full transactional insert graph.
 * Pure + synchronous. Entries are processed in (posted_on, Trans #) order —
 * matching the server pipeline's staging drain order — so FIFO application
 * and bill allocation reproduce the server behavior.
 *
 * `details` (optional) enables the two detail-report passes:
 *   • Trans#-keyed ENRICHMENT — detail rows join EXACTLY onto their compiled
 *     domain rows (no fuzzy amount/date matching) and fill richer
 *     category/memo/reference fields.
 *   • AUTO-CORRECTION (opt-in via details.autoCorrect) — detail rows missing
 *     from the journal are synthesized as flagged correcting entries.
 */
export function compileQbBundle(
	entries: ParsedEntry[],
	resolutions: QbCompileResolutions,
	options: QbCompileOptions,
	details?: QbCompileDetails,
): QbCompiledResult {
	const compiler = new Compiler(resolutions, options);

	// ── Phase 6: synthesize entries for detail rows the journal is missing ──
	const enrichment: QbEnrichmentStats = { arMatched: 0, arUnmatched: 0, arCorrected: 0, apMatched: 0, apUnmatched: 0, apCorrected: 0 };
	const knownTransIds = new Set(entries.map((e) => e.transactionId));
	const corrections: ParsedEntry[] = [];
	if (details?.autoCorrect) {
		compiler.clearingGl = details.autoCorrect.clearingGl;
		for (const kind of ['ar', 'ap'] as const) {
			for (const row of details[kind] ?? []) {
				if (!row.transId || knownTransIds.has(row.transId)) continue;
				const synthetic = buildCorrectionEntry(kind, row);
				if (!synthetic) continue;
				corrections.push(synthetic);
				knownTransIds.add(row.transId);
				if (kind === 'ar') enrichment.arCorrected++;
				else enrichment.apCorrected++;
			}
		}
	}

	const ordered = [...entries, ...corrections].sort((a, b) =>
		a.postedOn - b.postedOn || compareTransId(a.transactionId, b.transactionId));

	const byProjection: Record<string, number> = {};
	for (const entry of ordered) {
		const resolved = compiler.resolveLines(entry);
		const entryId = compiler.postEntry(entry, resolved);
		const cls = compiler.classify(entry, resolved);
		if (cls.type === 'unclassified') {
			compiler.auditEntry(entryId, entry, 'unclassified', cls.reason ?? 'no rule matched');
			continue;
		}
		try {
			compiler.project(cls.type, entry, resolved, entryId);
			byProjection[cls.type] = (byProjection[cls.type] ?? 0) + 1;
		} catch (e) {
			compiler.auditEntry(entryId, entry, 'error', `error: ${(e as Error).message}`);
		}
	}

	compiler.finalizeApplications();
	compiler.finalizeRows();

	// ── Phase 4: Trans#-keyed enrichment — exact joins, no fuzzy matching ──
	if (details?.ar || details?.ap) {
		enrichRows(compiler.rows, options.associationId, details, enrichment);
	}

	return {
		payloadVersion: QB_COMPILED_PAYLOAD_VERSION,
		associationId: options.associationId,
		rows: compiler.rows,
		audit: compiler.audit,
		unresolvedVendors: [...compiler.unresolvedVendors.values()],
		unresolvedOwners: [...compiler.unresolvedOwners.values()],
		enrichment: details ? enrichment : undefined,
		stats: {
			entries: compiler.rows.journal_entries.length,
			lines: compiler.rows.journal_lines.length,
			byProjection,
			unclassified: compiler.audit.length,
			banksCreated: compiler.banksCreated,
			bankEndingCents: compiler.bankEndingCents(),
		},
	};
}

/**
 * Join detail rows onto compiled domain rows by Trans # (via the source_ref
 * suffix every projected row carries) and fill the richer detail fields the
 * journal export lacks. Mutates rows in place; counts matches.
 */
function enrichRows(
	rows: Record<CompiledTable, CompiledRow[]>,
	associationId: string,
	details: QbCompileDetails,
	stats: QbEnrichmentStats,
): void {
	const refFor = (transId: string) => `${associationId}:${transId}`;
	const chargesByRef = new Map(rows.charges.map((r) => [String(r.source_ref), r]));
	const billsByRef = new Map(rows.bills.map((r) => [String(r.source_ref), r]));
	// payments/credits carry source_entry_id, not source_ref — map via entries.
	const entryIdByRef = new Map(rows.journal_entries.map((r) => [String(r.source_ref), String(r.id)]));
	const paymentsByEntry = new Map(rows.payments.map((r) => [String(r.source_entry_id), r]));
	const creditsByEntry = new Map(rows.credits.map((r) => [String(r.source_entry_id), r]));
	const billPaymentsByEntry = new Map(
		rows.bill_payments.filter((r) => r.source_entry_id != null).map((r) => [String(r.source_entry_id), r]),
	);

	for (const row of details.ar ?? []) {
		if (!row.transId) { stats.arUnmatched++; continue; }
		const ref = refFor(row.transId);
		const entryId = entryIdByRef.get(ref);
		let target: CompiledRow | undefined;
		if (row.type === 'Invoice') {
			target = chargesByRef.get(ref);
			if (target) {
				if (row.category) target.category = row.category;
				if (row.memo) target.memo = row.memo;
			}
		} else if (row.type === 'Payment') {
			target = entryId ? paymentsByEntry.get(entryId) : undefined;
			if (target && row.num) target.reference = row.num;
		} else if (row.type === 'Credit Memo') {
			target = entryId ? creditsByEntry.get(entryId) : undefined;
			if (target && row.num) target.credit_number = row.num;
		}
		if (target) stats.arMatched++;
		else stats.arUnmatched++;
	}

	for (const row of details.ap ?? []) {
		if (!row.transId) { stats.apUnmatched++; continue; }
		const ref = refFor(row.transId);
		const entryId = entryIdByRef.get(ref);
		let target: CompiledRow | undefined;
		if (row.type === 'Bill') {
			target = billsByRef.get(ref);
			if (target && row.memo) target.memo = row.memo;
		} else if (row.type === 'Bill Pmt -Check') {
			target = entryId ? billPaymentsByEntry.get(entryId) : undefined;
			if (target && row.num) target.reference = row.num;
		}
		if (target) stats.apMatched++;
		else stats.apUnmatched++;
	}
}
